import React, { useState, useEffect } from 'react';
import { 
  Box,
  Button,
  Card,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import { 
  Language as LanguageIcon,
  Refresh as RefreshIcon,
  TextFields as TextFieldsIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Check as CheckIcon,
  FileCopy as FileIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getKnowledgeBase, addKnowledgeBase, createDocument, deleteKnowledgeBase, getKnowledgeBaseById, uploadDocumentFile } from '../../redux/actions/knowledgeBaseActions';
import useCallOnce from '../../hooks/useCallOnce';
import { showSuccess, showError } from '../../Components/reuse/Toast';
import LinearLoading from '../../Components/reuse/LinearLoading';
import SearchBar from './components/SearchBar';
import KnowledgeBaseTable from './components/KnowledgeBaseTable';
import AddUrlDialog from './components/AddUrlDialog';
import AddTextDialog from './components/AddTextDialog';
import AddFileDialog from './components/AddFileDialog';
import ContextMenu from './components/ContextMenu';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import DocumentDetailSidebar from './components/DocumentDetailSidebar';

function KnowledgeBase() {
  const dispatch = useDispatch();
  const { loading, knowledgeBases } = useSelector((state) => state.knowledgeBase);
  const [isAdding, setIsAdding] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '', type: 'url' });
  const [formErrors, setFormErrors] = useState({});
  
  // Text dialog state
  const [isCreatingText, setIsCreatingText] = useState(false);
  const [openTextDialog, setOpenTextDialog] = useState(false);
  const [textFormData, setTextFormData] = useState({ name: '', text: '', type: 'text' });
  const [textFormErrors, setTextFormErrors] = useState({});
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [fileFormData, setFileFormData] = useState({ name: '', file: null, type: 'file' });
  const [fileFormErrors, setFileFormErrors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentDetailOpen, setDocumentDetailOpen] = useState(false);
  const [templateCreatedKeys, setTemplateCreatedKeys] = useState({});
  const [templateDeleteDialogOpen, setTemplateDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null); // { key?, kbId?, title }
  const [customMenuAnchor, setCustomMenuAnchor] = useState(null);

  // Define templates once to share with UI and reconciliation logic
  const templateDefs = [
    { key: 'contact', title: 'Contact Us', desc: 'Quickly add a "Contact Us" page of your brand.', badgeBg: '#e8f0fe', badgeColor: '#1a73e8' },
    { key: 'care', title: 'Customer Care', desc: 'Add a "Customer Care" document for customer support.', badgeBg: '#eceff1', badgeColor: '#607d8b' },
    { key: 'terms', title: 'Terms and Conditions', desc: 'Use this to add your brand\'s Terms and Conditions.', badgeBg: '#fde8e8', badgeColor: '#ef5350' },
    { key: 'privacy', title: 'Privacy Policy', desc: 'Set up a Privacy Policy of your brand.', badgeBg: '#fff6db', badgeColor: '#8d6e63' },
    { key: 'return', title: 'Return Policy', desc: 'Create a comprehensive Return Policy document.', badgeBg: '#e0f7fa', badgeColor: '#00acc1' },
  ];

  // On every list change (including initial load or after delete), compute which templates exist
  useEffect(() => {
    const list = Array.isArray(knowledgeBases) ? knowledgeBases : [];
    const nextFlags = {};
    templateDefs.forEach((t) => {
      nextFlags[t.key] = list.some((kb) => (kb?.name || '') === (t.title || ''));
    });
    setTemplateCreatedKeys(nextFlags);
  }, [knowledgeBases]);

  // (removed conflicting effect that wrote mismatched keys like 'contactus')

  // Fetch knowledge base data when component mounts
  useCallOnce('getKnowledgeBase', () => {
    dispatch(getKnowledgeBase());
  }, [dispatch]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormData({ name: '', url: '', type: 'url' });
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', url: '', type: 'url' });
    setFormErrors({});
  };

  // Text dialog handlers
  const handleOpenTextDialog = () => {
    setOpenTextDialog(true);
    setTextFormData({ name: '', text: '', type: 'text' });
    setTextFormErrors({});
  };

  const handleCloseTextDialog = () => {
    setOpenTextDialog(false);
    setTextFormData({ name: '', text: '', type: 'text' });
    setTextFormErrors({});
  };

  const handleOpenFileDialog = () => {
    setOpenFileDialog(true);
    setFileFormData({ name: '', file: null, type: 'file' });
    setFileFormErrors({});
  };

  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setFileFormData({ name: '', file: null, type: 'file' });
    setFileFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    // Disallow using predefined template names for custom adds
    const isCustomAdd = !formData.fromTemplateKey; // fromTemplateKey exists for template-based adds
    const reservedNames = templateDefs.map(t => (t.title || '').toLowerCase());
    if (isCustomAdd && reservedNames.includes((formData.name || '').toLowerCase())) {
      errors.name = 'This name is reserved for a template. Please use a different name.';
    }
    
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.url)) {
        errors.url = 'Please enter a valid URL starting with http:// or https://';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTextForm = () => {
    const errors = {};
    
    if (!textFormData.name.trim()) {
      errors.name = 'Text name is required';
    }
    // Disallow using predefined template names for custom adds
    const isCustomTextAdd = !textFormData.fromTemplateKey;
    const reservedTextNames = templateDefs.map(t => (t.title || '').toLowerCase());
    if (isCustomTextAdd && reservedTextNames.includes((textFormData.name || '').toLowerCase())) {
      errors.name = 'This name is reserved for a template. Please use a different name.';
    }
    
    if (!textFormData.text.trim()) {
      errors.text = 'Text content is required';
    }
    
    setTextFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFileForm = () => {
    const errors = {};

    if (!fileFormData.name.trim()) {
      errors.name = 'Document name is required';
    }
    const isCustomFileAdd = !fileFormData.fromTemplateKey;
    const reservedTextNames = templateDefs.map(t => (t.title || '').toLowerCase());
    if (isCustomFileAdd && reservedTextNames.includes((fileFormData.name || '').toLowerCase())) {
      errors.name = 'This name is reserved for a template. Please use a different name.';
    }

    if (!fileFormData.file) {
      errors.file = 'Document file is required';
    }

    setFileFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsAdding(true);
    try {
      const response = await dispatch(addKnowledgeBase(formData));
      
      if (response?.success) {
        showSuccess(response.message || 'Knowledge base added successfully!');
        handleCloseDialog();
        // Refresh the list
        dispatch(getKnowledgeBase());
        return true;
      }
    } catch (error) {
      console.error('Failed to add knowledge base:', error);
      
      // Show error toast with specific error message
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to add knowledge base. Please try again.';
      showError(errorMessage);
    } finally {
      setIsAdding(false);
    }
    return false;
  };

  const handleTextSubmit = async () => {
    if (!validateTextForm()) return;

    setIsCreatingText(true);
    try {
      const response = await dispatch(createDocument(textFormData));
      
      if (response?.success) {
        showSuccess(response.message || 'Text document created successfully!');
        handleCloseTextDialog();
        // Refresh the list
        dispatch(getKnowledgeBase());
        return true;
      }
    } catch (error) {
      console.error('Failed to create text document:', error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to create text document. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCreatingText(false);
    }
    return false;
  };

  const handleFileSubmit = async () => {
    if (!validateFileForm()) return;

    setIsUploadingFile(true);
    try {
      const payload = new FormData();
      payload.append('name', fileFormData.name);
      payload.append('type', 'file');
      if (fileFormData.file) {
        payload.append('file', fileFormData.file);
      }
      if (fileFormData.fromTemplateKey) {
        payload.append('fromTemplateKey', fileFormData.fromTemplateKey);
      }

      const response = await dispatch(uploadDocumentFile(payload));

      if (response?.success) {
        showSuccess(response.message || 'Document file uploaded successfully!');
        handleCloseFileDialog();
        dispatch(getKnowledgeBase());
        return true;
      }
    } catch (error) {
      console.error('Failed to upload document file:', error);

      const errorMessage = error?.response?.data?.message ||
                          error?.message ||
                          'Failed to upload document file. Please try again.';
      showError(errorMessage);
    } finally {
      setIsUploadingFile(false);
    }
    return false;
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleTextChange = (field) => (event) => {
    setTextFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (textFormErrors[field]) {
      setTextFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileChange = (field) => (event) => {
    let value = event;

    if (event && event.target) {
      if (field === 'file') {
        value = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
      } else {
        value = event.target.value;
      }
    }

    setFileFormData(prev => ({
      ...prev,
      [field]: value
    }));

    const errorKey = field === 'file' ? 'file' : field;
    if (fileFormErrors[errorKey]) {
      setFileFormErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    // Don't clear selectedItem here, keep it for the delete operation
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await dispatch(deleteKnowledgeBase(selectedItem.knowledgeBaseId));
      
      if (response?.success) {
        showSuccess(response.message || 'Document deleted successfully!');
        setDeleteDialogOpen(false);
        setSelectedItem(null);
        // Refresh the list to get latest data
        dispatch(getKnowledgeBase());
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to delete document. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDocumentClick = async (item) => {
    setDocumentDetailOpen(true);
    try {
      // The loading state is managed by Redux action
      await dispatch(getKnowledgeBaseById(item.knowledgeBaseId));
    } catch (error) {
      console.error('Failed to fetch document details:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch document details. Please try again.';
      showError(errorMessage);
      // Close the sidebar on error
      setDocumentDetailOpen(false);
    }
  };

  const handleTemplateDeleteClick = (templateKey) => {
    const templateDef = templateDefs.find(t => t.key === templateKey);
    setTemplateToDelete({ key: templateKey, title: templateDef?.title || 'Template' });
    setTemplateDeleteDialogOpen(true);
  };

  const handleCustomDeleteClick = (item) => {
    setTemplateToDelete({ kbId: item.knowledgeBaseId, title: item?.name || 'Document' });
    setTemplateDeleteDialogOpen(true);
  };

  const handleTemplateDeleteConfirm = async () => {
    if (!templateToDelete) return;

    const templateKey = templateToDelete.key;
    const kbIdDirect = templateToDelete.kbId;
    
    let knowledgeBaseIdToDelete = kbIdDirect;
    if (!knowledgeBaseIdToDelete) {
      // Find the knowledge base item that matches this template
      const templateItem = knowledgeBases.find(kb => {
        const templateDef = templateDefs.find(t => t.key === templateKey);
        return templateDef && (kb?.name || '') === (templateDef.title || '');
      });
      if (!templateItem) {
        showError('Template document not found');
        setTemplateDeleteDialogOpen(false);
        setTemplateToDelete(null);
        return;
      }
      knowledgeBaseIdToDelete = templateItem.knowledgeBaseId;
    }

    setIsDeleting(true);
    try {
      const response = await dispatch(deleteKnowledgeBase(knowledgeBaseIdToDelete));
      
      if (response?.success) {
        showSuccess(response.message || 'Template document deleted successfully!');
        // Update template created keys to reflect deletion
        if (templateKey) {
          setTemplateCreatedKeys(prev => ({ ...prev, [templateKey]: false }));
        }
        // Refresh the list to get latest data
        dispatch(getKnowledgeBase());
      }
    } catch (error) {
      console.error('Failed to delete template document:', error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to delete template document. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
      setTemplateDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleTemplateDeleteCancel = () => {
    setTemplateDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleTemplateView = async (templateKey) => {
    // Find the knowledge base item that matches this template
    const templateItem = knowledgeBases.find(kb => {
      const templateDef = templateDefs.find(t => t.key === templateKey);
      return templateDef && (kb?.name || '') === (templateDef.title || '');
    });

    if (!templateItem) {
      showError('Template document not found');
      return;
    }

    setDocumentDetailOpen(true);
    try {
      // The loading state is managed by Redux action
      await dispatch(getKnowledgeBaseById(templateItem.knowledgeBaseId));
    } catch (error) {
      console.error('Failed to fetch template document details:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch template document details. Please try again.';
      showError(errorMessage);
      // Close the sidebar on error
      setDocumentDetailOpen(false);
    }
  };

  const handleCustomView = async (item) => {
    setDocumentDetailOpen(true);
    try {
      await dispatch(getKnowledgeBaseById(item.knowledgeBaseId));
    } catch (error) {
      console.error('Failed to fetch document details:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch document details. Please try again.';
      showError(errorMessage);
      setDocumentDetailOpen(false);
    }
  };

  // Filter knowledge bases based on search term
  const filteredKnowledgeBases = knowledgeBases.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.knowledgeBaseId && item.knowledgeBaseId.toLowerCase().includes(searchLower)) ||
      (item.knowledgeBaseUrl && item.knowledgeBaseUrl.toLowerCase().includes(searchLower)) ||
      (item.userId && item.userId.toLowerCase().includes(searchLower))
    );
  });

  if(loading) return (
    <CircularProgress 
      size={20} 
      sx={{ position: 'absolute', top: '50%', left: '50%' }} 
    />
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Action Buttons Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        mb: 3,
        gap: 2
      }}>
        {/* <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => dispatch(getKnowledgeBase())}
          disabled={loading}
          sx={{
            backgroundColor: '#ffffff',
            borderColor: '#d1d5db',
            color: '#374151',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            '&:hover': { 
              backgroundColor: '#f9fafb',
              borderColor: '#9ca3af'
            },
            '&:disabled': {
              backgroundColor: '#f9fafb',
              borderColor: '#e5e7eb',
              color: '#9ca3af'
            }
          }}
        >
          Refresh
        </Button> */}
        {/* <Button
          variant="contained"
          startIcon={<LanguageIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: '#374151',
            color: '#ffffff',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid #000000',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': { 
              backgroundColor: '#1f2937',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            },
            '&:active': {
              backgroundColor: '#111827',
              transform: 'translateY(1px)'
            }
          }}
        >
          Add URL
        </Button>
        <Button
          variant="contained"
          startIcon={<TextFieldsIcon />}
          onClick={handleOpenTextDialog}
          sx={{
            backgroundColor: '#374151',
            color: '#ffffff',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid #000000',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': { 
              backgroundColor: '#1f2937',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            },
            '&:active': {
              backgroundColor: '#111827',
              transform: 'translateY(1px)'
            }
          }}
        >
          Create Text
        </Button> */}

        {/* Add Custom Menu */}
        <Button
          variant="contained"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setCustomMenuAnchor(e.currentTarget)}
          sx={{
            backgroundColor: '#374151',
            color: '#ffffff',
            borderRadius: '20px',
            px: 2,
            py: 1,
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid #000000',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': { 
              backgroundColor: '#1f2937',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
            },
            '&:active': {
              backgroundColor: '#111827',
              transform: 'translateY(1px)'
            }
          }}
        >
          Add Custom
        </Button>
        <Menu
          anchorEl={customMenuAnchor}
          open={Boolean(customMenuAnchor)}
          onClose={() => setCustomMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setCustomMenuAnchor(null); setOpenDialog(true); setFormData({ name: '', url: '', type: 'url' }); }}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            Add URL
          </MenuItem>
          <MenuItem onClick={() => { setCustomMenuAnchor(null); handleOpenTextDialog(); }}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            Create Text
          </MenuItem>
          <MenuItem onClick={() => { setCustomMenuAnchor(null); handleOpenFileDialog(); }}>
            <ListItemIcon>
              <FileIcon fontSize="small" />
            </ListItemIcon>
            Upload Document
          </MenuItem>
        </Menu>
      </Box>

        {/* {loading && <LinearLoading show={true} />} */}

        {/* Search Bar */}
        {/* <SearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredCount={filteredKnowledgeBases.length}
          totalCount={knowledgeBases.length}
        /> */}

        {/* Template Static Box */}
        <Box sx={{ pb: 2, pt: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
            Choose a template or customize it to fit your needs
          </Typography>
          <Grid container spacing={2}>
            {templateDefs.map((t) => (
              <Grid key={t.key} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                <Card sx={{ p: 2, position: 'relative', height: '100%', border: '1px solid #e5e7eb', bgcolor: '#ffffff', borderRadius: 2, '&:hover': { boxShadow: 3 }, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 700, px: 1, py: 0.5, borderRadius: '6px', backgroundColor: t.badgeBg, color: t.badgeColor }}>
                      {t.title}
                    </Typography>
                    {templateCreatedKeys[t.key] && (
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon sx={{ color: '#ffffff', fontSize: 14 }} />
                      </Box>
                    )}
                  </Box>
                  {!templateCreatedKeys[t.key] && (
                    <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, mt: 1, fontSize: '13px', flexGrow: 1 }}>
                      {t.desc}
                    </Typography>
                  )}
                  
                  {/* Template Action Buttons */}
                  {!templateCreatedKeys[t.key] ? (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<LinkIcon />}
                        onClick={() => {
                          setOpenDialog(true);
                          setFormData({ name: t.title || '', url: '', type: 'url', fromTemplateKey: t.key });
                        }}
                        sx={{
                          flex: 1,
                          backgroundColor: '#374151',
                          color: '#ffffff',
                          borderRadius: '8px',
                          py: 0.75,
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #000000',
                          boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                          '&:hover': { 
                            backgroundColor: '#1f2937',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          },
                          '&:active': {
                            backgroundColor: '#111827',
                            transform: 'translateY(1px)'
                          }
                        }}
                      >
                        Add Url
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<DescriptionIcon />}
                        onClick={() => {
                          setOpenTextDialog(true);
                          setTextFormData({ name: t.title || '', text: '', type: 'text', fromTemplateKey: t.key });
                        }}
                        sx={{
                          flex: 1,
                          backgroundColor: '#374151',
                          color: '#ffffff',
                          borderRadius: '8px',
                          py: 0.75,
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #000000',
                          boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                          '&:hover': { 
                            backgroundColor: '#1f2937',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          },
                          '&:active': {
                            backgroundColor: '#111827',
                            transform: 'translateY(1px)'
                          }
                        }}
                      >
                        Add Text
                      </Button>
                    </Box>
                  ) : (
                    (() => {
                      const kb = knowledgeBases.find(k => (k?.name || '') === (t.title || '')) || {};
                      return (
                        <>
                          {/* <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, mt: 1 }}>
                            ID: {kb.knowledgeBaseId || '-'}
                          </Typography> */}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#111827', 
                              fontWeight: 600, 
                              mt: 0.5, 
                              fontSize: '13px', 
                              flexGrow: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4
                            }}
                            title={(kb.knowledgeBaseUrl || kb.knowledgeBaseText || '')}
                          >
                            {kb.knowledgeBaseUrl || kb.knowledgeBaseText || ''}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={<ViewIcon />}
                              onClick={() => handleTemplateView(t.key)}
                              sx={{
                                flex: 1,
                                backgroundColor: '#374151',
                                color: '#ffffff',
                                borderRadius: '8px',
                                py: 0.75,
                                textTransform: 'none',
                                fontSize: '11px',
                                fontWeight: 600,
                                border: '1px solid #000000',
                                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                                '&:hover': { 
                                  backgroundColor: '#1f2937',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                },
                                '&:active': {
                                  backgroundColor: '#111827',
                                  transform: 'translateY(1px)'
                                }
                              }}
                            >
                              View
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleTemplateDeleteClick(t.key)}
                              disabled={isDeleting}
                              sx={{
                                flex: 1,
                                backgroundColor: '#dc2626',
                                color: '#ffffff',
                                borderRadius: '8px',
                                py: 0.75,
                                textTransform: 'none',
                                fontSize: '11px',
                                fontWeight: 600,
                                border: '1px solid #b91c1c',
                                boxShadow: '0 2px 4px -1px rgba(220, 38, 38, 0.3)',
                                '&:hover': { 
                                  backgroundColor: '#b91c1c',
                                  boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.4)'
                                },
                                '&:active': {
                                  backgroundColor: '#991b1b',
                                  transform: 'translateY(1px)'
                                },
                                '&:disabled': {
                                  backgroundColor: '#fca5a5',
                                  borderColor: '#fca5a5',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </>
                      );
                    })()
                  )}
                </Card>
              </Grid>
            ))}
            {filteredKnowledgeBases
              .filter((kb) => !templateDefs.some((t) => (kb?.name || '') === (t.title || '')))
              .map((kb) => (
                <Grid key={kb.knowledgeBaseId} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                  <Card sx={{ p: 2, position: 'relative', height: '100%', border: '1px solid #e5e7eb', bgcolor: '#ffffff', borderRadius: 2, '&:hover': { boxShadow: 3 }, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography component="span" sx={{ fontSize: '14px', fontWeight: 700, px: 1, py: 0.5, borderRadius: '6px', backgroundColor: '#eef2ff', color: '#3730a3' }}>
                        {kb.name || '-'}
                      </Typography>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon sx={{ color: '#ffffff', fontSize: 14 }} />
                      </Box>
                    </Box>
                    {/* <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, mt: 1 }}>
                      ID: {kb.knowledgeBaseId}
                    </Typography> */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#111827', 
                        fontWeight: 600, 
                        mt: 0.5, 
                        fontSize: '13px', 
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4
                      }}
                      title={(kb.knowledgeBaseUrl || kb.knowledgeBaseText || '')}
                    >
                      {kb.knowledgeBaseUrl || kb.knowledgeBaseText || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<ViewIcon />}
                        onClick={() => handleCustomView(kb)}
                        sx={{
                          flex: 1,
                          backgroundColor: '#374151',
                          color: '#ffffff',
                          borderRadius: '8px',
                          py: 0.75,
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #000000',
                          boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                          '&:hover': { backgroundColor: '#1f2937' },
                          '&:active': { backgroundColor: '#111827', transform: 'translateY(1px)' }
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleCustomDeleteClick(kb)}
                        disabled={isDeleting}
                        sx={{
                          flex: 1,
                          backgroundColor: '#dc2626',
                          color: '#ffffff',
                          borderRadius: '8px',
                          py: 0.75,
                          textTransform: 'none',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #b91c1c',
                          boxShadow: '0 2px 4px -1px rgba(220, 38, 38, 0.3)',
                          '&:hover': { backgroundColor: '#b91c1c' },
                          '&:active': { backgroundColor: '#991b1b', transform: 'translateY(1px)' },
                          '&:disabled': { backgroundColor: '#fca5a5', borderColor: '#fca5a5', color: '#ffffff' }
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Knowledge Base Table */}
        {/* <KnowledgeBaseTable 
          filteredKnowledgeBases={filteredKnowledgeBases}
          searchTerm={searchTerm}
          onDocumentClick={handleDocumentClick}
          onMenuOpen={handleMenuOpen}
        /> */}

        {/* Add URL Dialog */}
        <AddUrlDialog 
          open={openDialog}
          onClose={handleCloseDialog}
          formData={formData}
          formErrors={formErrors}
          isAdding={isAdding}
          onChange={handleChange}
          onSubmit={async () => {
            const usedKey = formData.fromTemplateKey;
            const res = await handleSubmit();
            if (res !== false && usedKey) {
              setTemplateCreatedKeys(prev => ({ ...prev, [usedKey]: true }));
            }
          }}
        />

        {/* Add Text Dialog */}
        <AddTextDialog 
          open={openTextDialog}
          onClose={handleCloseTextDialog}
          formData={textFormData}
          formErrors={textFormErrors}
          isAdding={isCreatingText}
          onChange={handleTextChange}
          onSubmit={async () => {
            const usedKey = textFormData.fromTemplateKey;
            const res = await handleTextSubmit();
            if (res !== false && usedKey) {
              setTemplateCreatedKeys(prev => ({ ...prev, [usedKey]: true }));
            }
          }}
        />

        {/* Add File Dialog */}
        <AddFileDialog
          open={openFileDialog}
          onClose={handleCloseFileDialog}
          formData={fileFormData}
          formErrors={fileFormErrors}
          isUploading={isUploadingFile}
          onChange={handleFileChange}
          onSubmit={async () => {
            const usedKey = fileFormData.fromTemplateKey;
            const res = await handleFileSubmit();
            if (res !== false && usedKey) {
              setTemplateCreatedKeys(prev => ({ ...prev, [usedKey]: true }));
            }
          }}
        />

        {/* Context Menu */}
        <ContextMenu 
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onCopyId={() => {
            if (selectedItem) {
              navigator.clipboard.writeText(selectedItem.knowledgeBaseId);
              showSuccess('Document ID copied to clipboard!');
              handleMenuClose();
            }
          }}
          onDeleteClick={handleDeleteClick}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog 
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          selectedItem={selectedItem}
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
        />

        {/* Document Detail Sidebar */}
        <DocumentDetailSidebar 
          open={documentDetailOpen}
          onClose={() => setDocumentDetailOpen(false)}
        />

        {/* Template Delete Confirmation Dialog */}
        <Dialog
          open={templateDeleteDialogOpen}
          onClose={handleTemplateDeleteCancel}
          aria-labelledby="template-delete-dialog-title"
          aria-describedby="template-delete-dialog-description"
        >
          <DialogTitle id="template-delete-dialog-title">
            Delete Template Document
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="template-delete-dialog-description">
              Are you sure you want to delete the "{templateToDelete?.title}" document? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleTemplateDeleteCancel}
              disabled={isDeleting}
              sx={{
                color: '#fff',
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTemplateDeleteConfirm}
              disabled={isDeleting}
              variant="contained"
              sx={{
                backgroundColor: '#dc2626',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                border: '1px solid #b91c1c',
                '&:hover': { 
                  backgroundColor: '#b91c1c'
                },
                '&:disabled': {
                  backgroundColor: '#fca5a5',
                  borderColor: '#fca5a5'
                }
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
}

export default KnowledgeBase;
